import { NotificationEventEnum } from '.'
import { CASStatus } from '../statuses/cas'
import { NotificationEventItem } from './NotificationEventItem'
import {
  AllApprovalsNotificationEventItem,
  PartialApprovalNotificationEventItem,
} from './common/approval'
import { CommentedNotificationEventItem } from './common/commented'
import { DispositionGivenNotificationEventItem } from './common/disposition'
import { EditedNotificationEventItem } from './common/edited'
import { NumberNameEditedNotificationEventItem } from './common/numberName'
import { RevertedNotificationEventItem } from './common/reverted'

const cas = 'Construction & Admin Submittal' as const

const Submitted = new NotificationEventItem({
  id: '5C374E10-ABBE-4C85-A09B-4A4D042449B3',
  name: 'Submitted',
  description: `A new ${cas} is submitted for the workflow`,
})
const Resubmitted = new NotificationEventItem({
  id: '2D78BC2D-97AA-4FA6-9DA1-72F8A0155312',
  name: 'Resubmitted',
  description: `An existing ${cas} is resubmitted for the workflow`,
})
const RoutedToGroupReview = new NotificationEventItem({
  id: 'F0D6FE26-802A-42F7-A5DF-FB8526DBF518',
  name: 'Routed to Group Review',
  description: `A router determines the ${cas} is ready for ${CASStatus.GroupReview.name}`,
})
const RoutingGroupAccepted = PartialApprovalNotificationEventItem({
  id: '6AB3B6FC-CB4B-41A9-8E7F-22CBC741DB33',
  name: 'Routing Group Accepted',
  description: { object: cas, group: 'Routing Group' },
})
const AllRoutingGroupsAccepted = AllApprovalsNotificationEventItem({
  id: '19C66E03-BDD6-4840-8C79-04DF8057D61F',
  name: 'All Routing Groups Accepted',
  description: { object: cas, group: 'Routing Group' },
})
const RoutedToREReview = new NotificationEventItem({
  id: 'DDEF5B86-DFAF-4342-8AF9-166D4A54E8C7',
  name: 'Routed to RE Review',
  description: `A router determines the ${cas} is ready for ${CASStatus.REReview.name}`,
})
const DispositionGiven = DispositionGivenNotificationEventItem({
  id: 'B1BFA167-A75B-4A8C-B9B7-2AAC58A98516',
  submittal: cas,
})
const Reverted = RevertedNotificationEventItem({
  id: '9E77AE93-AE1C-4D13-A248-145FDBDE421F',
  revertable: cas,
  from: [
    CASStatus.Closed,
    CASStatus.Unlocked,
    CASStatus.GroupReview,
    CASStatus.REReview,
  ],
  to: [CASStatus.RouterReview, CASStatus.GroupReview, CASStatus.REReview],
})
const RevisionsRequested = new NotificationEventItem({
  id: '12E6BDAB-C940-4F3D-B364-F98FF573F400',
  name: 'Revisions Requested',
  description: `A ${cas} does not meet expectations in its current form`,
})
const NumberOrNameEdited = NumberNameEditedNotificationEventItem({
  id: '4A3742EC-1815-4E0F-9907-7AC4EF911107',
})
const Edited = EditedNotificationEventItem({
  id: '48076A04-8DEA-4D62-A4D6-EBA5AFD4BC16',
  editable: cas,
})
const Commented = CommentedNotificationEventItem({
  id: '3F857E2D-DF24-411B-9DA5-D5542732E389',
  commentable: cas,
})

const RequestedReview = new NotificationEventItem({
  id: 'FDF0BCDC-F330-4CD3-8E55-78577254CC82',
  name: 'Requested Review Upcoming',
  description: `The Requested Review Date of a ${cas} has reached a notification threshold`,
})

export type CASNotificationEventItem =
  | typeof Submitted
  | typeof Resubmitted
  | typeof RoutedToGroupReview
  | typeof RoutingGroupAccepted
  | typeof AllRoutingGroupsAccepted
  | typeof RoutedToREReview
  | typeof DispositionGiven
  | typeof Reverted
  | typeof RevisionsRequested
  | typeof NumberOrNameEdited
  | typeof Edited
  | typeof Commented
  | typeof RequestedReview

class CASNotificationEventEnum extends NotificationEventEnum<
  'CASNotificationEvent',
  CASNotificationEventItem,
  CASNotificationEventItem['name'],
  CASNotificationEventItem['id']
> {
  readonly typename = 'CASNotificationEvent'
  get values() {
    return [
      this.Submitted,
      this.Resubmitted,
      this.RoutedToGroupReview,
      this.RoutingGroupAccepted,
      this.AllRoutingGroupsAccepted,
      this.RoutedToREReview,
      this.DispositionGiven,
      this.Reverted,
      this.RevisionsRequested,
      this.NumberOrNameEdited,
      this.Edited,
      this.Commented,
      this.RequestedReview,
    ]
  }
  readonly Submitted = Submitted
  readonly Resubmitted = Resubmitted
  readonly RoutedToGroupReview = RoutedToGroupReview
  readonly RoutingGroupAccepted = RoutingGroupAccepted
  readonly AllRoutingGroupsAccepted = AllRoutingGroupsAccepted
  readonly RoutedToREReview = RoutedToREReview
  readonly DispositionGiven = DispositionGiven
  readonly Reverted = Reverted
  readonly RevisionsRequested = RevisionsRequested
  readonly NumberOrNameEdited = NumberOrNameEdited
  readonly Edited = Edited
  readonly Commented = Commented
  readonly RequestedReview = RequestedReview
}

export const CASNotificationEvent = new CASNotificationEventEnum()
